import type { Crumb } from "./routeMap";
import { useGuardedNavigate } from "./NavigationGuard";
import type { ReactNode } from "react";
import css from "./studio.module.css";

/**
 * The breadcrumb row — FR-2, FR-3.
 *
 * Present on **every** Studio route, not only deep ones, so its absence never
 * has to mean anything. Left: the trail. Right: whatever state the screen wants
 * printed beside it.
 *
 * Ancestors are real `<Link>`s rather than buttons styled as links (NFR-5), so
 * middle-click, copy-link and open-in-new-tab all work — a breadcrumb that
 * cannot be opened in a new tab is a button wearing a costume.
 */
export function Breadcrumb({
  crumbs,
  status,
}: {
  crumbs: Crumb[];
  /** Mono state line on the right. Optional: the asset list has nothing to say. */
  status?: ReactNode;
}) {
  const guardedNavigate = useGuardedNavigate();

  if (crumbs.length === 0) return null;

  return (
    <div className={css.crumbRow}>
      {/* A landmark, so assistive technology can skip to it (NFR-6). */}
      <nav aria-label="Breadcrumb" className={css.crumbNav}>
        <ol className={css.crumbList}>
          {crumbs.map((crumb, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <li key={`${crumb.label}-${i}`} className={css.crumbItem}>
                {i > 0 && (
                  // Decorative: the list structure already conveys the nesting,
                  // so a screen reader announcing "slash" three times is noise.
                  <span className={css.crumbSep} aria-hidden="true">
                    /
                  </span>
                )}
                {crumb.href ? (
                  // An anchor, not a Link: real href for middle-click and
                  // copy-link (NFR-5), plain left click through the guard.
                  <a
                    href={crumb.href}
                    className={css.crumbLink}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                      e.preventDefault();
                      guardedNavigate(crumb.href!);
                    }}
                  >
                    {crumb.label}
                  </a>
                ) : (
                  <span
                    className={isLast ? css.crumbCurrent : css.crumbInert}
                    // Only the genuine last segment is the current page. A
                    // middle crumb can lose its href simply for pointing at
                    // where we already are (routeMap's self-link rule), and
                    // claiming two current pages would be a lie.
                    aria-current={isLast ? "page" : undefined}
                  >
                    {crumb.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
      {status && <div className={css.crumbStatus}>{status}</div>}
    </div>
  );
}
